/**
 * Framework-agnostic "wait for the user to sign in to Provider Portal" gate.
 * Extracted out of MemberProfilePage so the resolver/idempotency mechanics
 * are directly unit-testable without React.
 *
 * Single-slot AND idempotent: while a login is pending, a second `request()`
 * call (e.g. Create's resolvePpMemberId path firing concurrently with an
 * onError-triggered retry-prompt from Duplicate/Modify) returns the SAME
 * promise instead of opening a second modal / orphaning the first resolver.
 */

export const PP_LOGIN_CANCELLED_MESSAGE = "member-360:pp-login-cancelled";

/** Thrown when the user explicitly cancels the PP login prompt. */
export class PpLoginCancelledError extends Error {
  constructor() {
    super(PP_LOGIN_CANCELLED_MESSAGE);
    this.name = "PpLoginCancelledError";
  }
}

export interface PpLoginGateHandlers {
  /** Surface the login UI. Called at most once per pending request cycle. */
  onOpen: () => void;
}

export class PpLoginGate {
  private pending: Promise<void> | null = null;
  private resolver: { resolve: () => void; reject: (e: Error) => void } | null = null;

  constructor(private readonly handlers: PpLoginGateHandlers) {}

  /** True while a login prompt is open and unresolved. */
  get isPending(): boolean {
    return this.pending !== null;
  }

  /**
   * Ensures a login is in flight and returns a promise that resolves on
   * `succeed()` or rejects with `PpLoginCancelledError` on `cancel()`.
   * Concurrent callers while one is already pending share this SAME promise
   * — `onOpen` fires at most once per cycle.
   */
  request(): Promise<void> {
    if (this.pending) return this.pending;
    this.pending = new Promise<void>((resolve, reject) => {
      this.resolver = { resolve, reject };
      this.handlers.onOpen();
    }).finally(() => {
      this.pending = null;
      this.resolver = null;
    });
    return this.pending;
  }

  /** Resolves the in-flight request (no-op if none pending). */
  succeed(): void {
    this.resolver?.resolve();
  }

  /** Rejects the in-flight request with PpLoginCancelledError (no-op if none pending). */
  cancel(): void {
    this.resolver?.reject(new PpLoginCancelledError());
  }
}
